import React from 'react';

import { DraggableLayout } from './components/Draggable';
import { StaticLayout } from './components/Static';
import { PERCENTAGE_SCALE } from './utils/constants';

type CustomControlsProps = {
	visible?: boolean;
	isDraggable?: boolean;
	showMinimize?: boolean;
	showVolume?: boolean;
	volume: number;
	duration: number;
	playing: boolean;
	minimized: boolean;
	playbackRate: number;
	fullscreen?: boolean;
	playedSeconds: number;
	showFullscreen?: boolean;
	onTogglePlay: () => void;
	onToggleSound: () => void;
	onToggleMinimized: () => void;
	onToggleFullscreen?: () => void;
	onSeek: (seconds: number) => void;
	onVolumeChange: (volume: number) => void;
	onPlaybackRateChange: (rate: number) => void;
};

function calculateProgress(playedSeconds: number, duration: number): number {
	return duration > 0 ? (playedSeconds / duration) * PERCENTAGE_SCALE : 0;
}

function CustomControlsComponent({
	visible = true,
	isDraggable = false,
	showMinimize = true,
	showVolume = true,
	showFullscreen = true,
	fullscreen = false,
	volume,
	playing,
	duration,
	minimized,
	playbackRate,
	playedSeconds,
	onSeek,
	onTogglePlay,
	onToggleSound,
	onVolumeChange,
	onToggleMinimized,
	onToggleFullscreen,
	onPlaybackRateChange
}: Readonly<CustomControlsProps>): JSX.Element {
	const progress = calculateProgress(playedSeconds, duration);

	function handleSliderChange(value: number): void {
		if (!Number.isFinite(duration) || duration <= 0) {
			return;
		}

		const newTime = (value / PERCENTAGE_SCALE) * duration;
		onSeek(newTime);
	}

	const layoutProps = {
		visible,
		volume,
		playing,
		minimized,
		playbackRate,
		playedSeconds,
		duration,
		progress,
		fullscreen,
		onTogglePlay,
		onToggleSound,
		showFullscreen,
		showVolume,
		onToggleMinimized,
		onToggleFullscreen,
		onVolumeChange,
		onPlaybackRateChange,
		onSliderChange: handleSliderChange
	};

	if (isDraggable) {
		return <DraggableLayout {...layoutProps} />;
	}

	return <StaticLayout {...layoutProps} showMinimize={showMinimize} />;
}

export const CustomControls = React.memo(CustomControlsComponent);
